import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteContentProvider } from "@/context/SiteContentContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Feed from "@/pages/Feed";
import Explore from "@/pages/Explore";
import Journal from "@/pages/Journal";
import EditorsLetter from "@/pages/EditorsLetter";
import Rooms from "@/pages/Rubriques";
import Section from "@/pages/Section";
import Profile from "@/pages/Profile";
import PostDetail from "@/pages/PostDetail";
import Compose from "@/pages/Compose";
import Messages from "@/pages/Messages";
import Settings from "@/pages/Settings";
import FounderPortal from "@/pages/FounderPortal";
import Install from "@/pages/Install";
import About from "@/pages/About";
import ComingSoon from "@/pages/ComingSoon";
import Live from "@/pages/Live";
import Broadcast from "@/pages/Broadcast";
import { Guidelines, Terms, Privacy } from "@/pages/LegalPages";
import { ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/AuthFlows";
import { InstallBanner } from "@/components/InstallBanner";
import { BottomTabBar } from "@/components/BottomTabBar";
import { CookieBanner } from "@/components/CookieBanner";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 80, textAlign: "center" }}>Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 80, textAlign: "center" }}>Loading…</div>;
  if (user) return <Navigate to="/feed" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <SiteContentProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors closeButton />
          <InstallBanner />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />

            <Route path="/feed" element={<Protected><Feed /></Protected>} />
            <Route path="/explore" element={<Protected><Explore /></Protected>} />
            <Route path="/journal" element={<Protected><Journal /></Protected>} />
            <Route path="/editors-letter" element={<Protected><EditorsLetter /></Protected>} />
            <Route path="/rooms" element={<Protected><Rooms /></Protected>} />
            <Route path="/room/:slug" element={<Protected><Section /></Protected>} />
            {/* Legacy redirects from old French naming */}
            <Route path="/rubriques" element={<Navigate to="/rooms" replace />} />
            <Route path="/rubrique/:slug" element={<Protected><Section /></Protected>} />
            <Route path="/profile/:username" element={<Protected><Profile /></Protected>} />
            <Route path="/post/:id" element={<Protected><PostDetail /></Protected>} />
            <Route path="/compose" element={<Protected><Compose /></Protected>} />
            <Route path="/messages" element={<Protected><Messages /></Protected>} />
            <Route path="/messages/:id" element={<Protected><Messages /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/favorite-rooms" element={<Navigate to="/rooms" replace />} />
            <Route path="/founder" element={<Protected><FounderPortal /></Protected>} />
            <Route path="/install" element={<Install />} />
            <Route path="/about" element={<About />} />

            {/* /live is member-only. /broadcast/:id stays public so the
                Landing teaser video works for curious guests. */}
            <Route path="/live" element={<Protected><Live /></Protected>} />
            <Route path="/broadcast/:id" element={<Broadcast />} />
            <Route path="/marketplace" element={<ComingSoon section="marketplace" />} />
            <Route path="/events" element={<ComingSoon section="events" />} />

            {/* Legal pages (Workshop-editable) */}
            <Route path="/guidelines" element={<Guidelines />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />

            {/* Auth flows */}
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password/:token" element={<PublicOnly><ResetPassword /></PublicOnly>} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <BottomTabBar />
          <CookieBanner />
        </BrowserRouter>
      </SiteContentProvider>
    </AuthProvider>
  );
}

export default App;
